import React, { useState, useEffect } from "react";
import Header from "../../components/Header";
import Sidebar from "../../components/Sidebar";
import { Link, useNavigate } from "react-router-dom";
import { FaChevronRight } from "react-icons/fa";
import "../../assets/styles/Style.css";
import "../../assets/styles/inmueble-form.css";
import { BaseInput, BaseSelect, BaseButton, BaseTextarea } from "../../components/ui";
import { useAuth } from "../../utils/AuthContext";
import inmuebleService from "../../services/inmuebleService";
import personaService from "../../services/personaService";
import tipoInmuebleService from "../../services/tipoInmuebleService";
import LocationMapPicker from "../../components/maps/LocationMapPicker";
import geocodingService from "../../services/geocodingService";
import { hasValidCoordinates } from "../../components/maps/openStreetMapUtils";
import Swal from "sweetalert2";

const selectFieldChange = (name, value, setFormData, setFormErrors, validateField) => {
  setFormData((prev) => ({ ...prev, [name]: value }));
  setFormErrors((prev) => ({ ...prev, [name]: validateField(name, value) }));
};

const FormSection = ({ title, description, children }) => (
  <section className="inmueble-form__section">
    <div className="inmueble-form__section-head">
      <h5>{title}</h5>
      {description && <p>{description}</p>}
    </div>
    <div className="row g-3">{children}</div>
  </section>
);

const AnadirInmueble = () => {
  const { estaAutenticado } = useAuth();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    propietario_id: "",
    tipoInmueble_id: "",
    nombre: "",
    descripcion: "",
    direccion: "",
    ubigeo: "",
    city: "",
    province: "",
    country: "Perú",
    latitude: "",
    longitude: "",
    show_on_map: true,
    cantidad_pisos: "",
    tiene_sotanos: false,
    cantidad_sotanos: "",
  });

  const [propietarios, setPropietarios] = useState([]);
  const [tiposInmueble, setTiposInmueble] = useState([]);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (!estaAutenticado) {
      navigate("/login");
    }

    const fetchData = async () => {
      try {
        const personas = await personaService.obtenerPersonas();
        setPropietarios(personas.filter((p) => p.rol === "propietario"));
        setTiposInmueble(await tipoInmuebleService.obtenertipoInmueble());
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchData();
  }, [estaAutenticado, navigate]);

  const validateField = (name, value) => {
    switch (name) {
      case "propietario_id":
        return value ? "" : "El propietario es requerido";
      case "tipoInmueble_id":
        return value ? "" : "El tipo de inmueble es requerido";
      case "nombre":
        return value ? "" : "El nombre es requerido";
      case "direccion":
        return value ? "" : "La dirección es requerida";
      case "descripcion":
        return value ? "" : "La descripción es requerida";
      case "ubigeo":
        return value ? "" : "El ubigeo es requerido";
      case "cantidad_pisos":
        return value && !isNaN(value) && Number(value) > 0
          ? ""
          : "Indique una cantidad válida de pisos";
      case "cantidad_sotanos":
        return !formData.tiene_sotanos || (value && !isNaN(value) && Number(value) > 0)
          ? ""
          : "Indique la cantidad de sótanos";
      default:
        return "";
    }
  };

  const validateForm = () => {
    const errors = {};
    Object.keys(formData).forEach((key) => {
      const error = validateField(key, formData[key]);
      if (error) errors[key] = error;
    });

    if (formData.show_on_map && !hasValidCoordinates(formData.latitude, formData.longitude)) {
      errors.latitude = "Marque la ubicación del edificio en el mapa";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === "checkbox" ? checked : value;

    if (name === "tiene_sotanos" && !checked) {
      setFormData((prev) => ({ ...prev, [name]: newValue, cantidad_sotanos: "" }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: newValue }));
    }

    setFormErrors((prev) => ({ ...prev, [name]: validateField(name, newValue) }));
  };

  const handleSelectField = (name) => (e) => {
    selectFieldChange(name, e.target.value, setFormData, setFormErrors, validateField);
  };

  const handleMapLocationChange = (location) => {
    setFormData((prev) => ({
      ...prev,
      latitude: location.latitude ?? prev.latitude,
      longitude: location.longitude ?? prev.longitude,
      city: location.city ?? prev.city,
      province: location.province ?? prev.province,
      country: location.country ?? prev.country,
    }));

    if (hasValidCoordinates(location.latitude, location.longitude)) {
      setFormErrors((prev) => ({ ...prev, latitude: "", longitude: "" }));
    }
  };

  const handleGeocodeAddress = async () => {
    const query = [formData.direccion, formData.city, formData.country].filter(Boolean).join(", ");
    if (!query.trim()) {
      Swal.fire({
        title: "Dirección requerida",
        text: "Ingresa la dirección para buscar en el mapa.",
        icon: "info",
      });
      return;
    }

    try {
      handleMapLocationChange(await geocodingService.search(query));
    } catch {
      Swal.fire({
        title: "Ubicación no encontrada",
        text: "Marca el punto manualmente en el mapa.",
        icon: "warning",
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      Swal.fire({
        title: "Campos incompletos",
        text: "Revisa los campos marcados antes de guardar.",
        icon: "warning",
      });
      return;
    }

    try {
      const dataToSend = {
        ...formData,
        cantidad_sotanos: formData.tiene_sotanos ? Number(formData.cantidad_sotanos) : 0,
        latitude: formData.latitude !== "" ? Number(formData.latitude) : null,
        longitude: formData.longitude !== "" ? Number(formData.longitude) : null,
        show_on_map: Boolean(formData.show_on_map),
      };
      delete dataToSend.tiene_sotanos;

      const response = await inmuebleService.crearInmueble(dataToSend);

      if (response) {
        Swal.fire({
          title: "¡Éxito!",
          text: "El inmueble ha sido registrado correctamente",
          icon: "success",
        }).then(() => navigate("/inmueble-registros"));
        return;
      }

      Swal.fire({
        title: "Error",
        text: "No se pudo registrar el inmueble. Inténtalo de nuevo.",
        icon: "error",
      });
    } catch (error) {
      console.error("Error al añadir el inmueble:", error);
      Swal.fire({
        title: "Error",
        text: "No se pudo registrar el inmueble. Inténtalo de nuevo.",
        icon: "error",
      });
    }
  };

  const handleCancel = () => {
    Swal.fire({
      title: "¿Cancelar registro?",
      text: "Los datos no guardados se perderán.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Sí, salir",
      cancelButtonText: "Seguir editando",
    }).then((result) => {
      if (result.isConfirmed) navigate("/inmueble-registros");
    });
  };

  const propietarioOptions = propietarios.map((p) => ({
    value: p.id,
    label: `${p.nombre} ${p.apellido}`,
  }));

  const tipoOptions = tiposInmueble.map((tipo) => ({
    value: tipo.id,
    label: tipo.nombre,
  }));

  return (
    <div className="inmueble-form-page">
      <Header />
      <Sidebar id="menu-item1" id1="menu-items1" activeClassName="inmueble-anadir" />
      <div className="page-wrapper">
        <div className="content">
          <div className="page-header">
            <div className="row align-items-center">
              <div className="col-sm-12 col-md">
                <ul className="breadcrumb mb-2">
                  <li className="breadcrumb-item">
                    <Link to="/inmueble-registros">Inmuebles</Link>
                  </li>
                  <li className="breadcrumb-item">
                    <FaChevronRight size={10} />
                  </li>
                  <li className="breadcrumb-item active">Nuevo edificio</li>
                </ul>
                <h1 className="page-title">Registrar inmueble</h1>
                <p className="page-subtitle">
                  Completa los datos del edificio y marca su ubicación para el mapa público.
                </p>
              </div>
            </div>
          </div>

          <div className="row">
            <div className="col-12">
              <div className="card inmueble-form-card">
                <form onSubmit={handleSubmit} noValidate>
                  <FormSection
                    title="Datos generales"
                    description="Información básica del edificio y su clasificación."
                  >
                    <div className="col-12 col-lg-8">
                      <BaseInput
                        label="Nombre del inmueble"
                        name="nombre"
                        required
                        value={formData.nombre}
                        onChange={handleChange}
                        placeholder="Edificio Los Pinos"
                        error={formErrors.nombre}
                        valid={!formErrors.nombre && Boolean(formData.nombre)}
                      />
                    </div>

                    <div className="col-12 col-lg-4">
                      <BaseSelect
                        label="Tipo"
                        name="tipoInmueble_id"
                        required
                        isSearchable
                        options={tipoOptions}
                        value={formData.tipoInmueble_id}
                        onChange={handleSelectField('tipoInmueble_id')}
                        placeholder="Seleccionar"
                        error={formErrors.tipoInmueble_id}
                      />
                    </div>

                    <div className="col-12 col-lg-6">
                      <BaseSelect
                        label="Propietario"
                        name="propietario_id"
                        required
                        isSearchable
                        options={propietarioOptions}
                        value={formData.propietario_id}
                        onChange={handleSelectField('propietario_id')}
                        placeholder="Seleccionar propietario"
                        error={formErrors.propietario_id}
                      />
                    </div>

                    <div className="col-12 col-lg-3">
                      <BaseInput
                        label="Cantidad de pisos"
                        name="cantidad_pisos"
                        type="number"
                        required
                        min="1"
                        restrict="number"
                        value={formData.cantidad_pisos}
                        onChange={handleChange}
                        placeholder="Ej. 8"
                        error={formErrors.cantidad_pisos}
                      />
                    </div>

                    <div className="col-12 col-lg-3">
                      <div className="inmueble-form__sotano-box">
                        <label>
                          <input
                            type="checkbox"
                            name="tiene_sotanos"
                            checked={formData.tiene_sotanos}
                            onChange={handleChange}
                          />
                          Tiene sótanos
                        </label>
                        {formData.tiene_sotanos && (
                          <BaseInput
                            label="Cantidad de sótanos"
                            name="cantidad_sotanos"
                            type="number"
                            required
                            min="1"
                            restrict="number"
                            value={formData.cantidad_sotanos}
                            onChange={handleChange}
                            placeholder="Ej. 2"
                            error={formErrors.cantidad_sotanos}
                            wrapperClassName="mt-3"
                          />
                        )}
                      </div>
                    </div>

                    <div className="col-12">
                      <BaseTextarea
                        label="Descripción"
                        name="descripcion"
                        required
                        rows={3}
                        value={formData.descripcion}
                        onChange={handleChange}
                        placeholder="Breve descripción del edificio, servicios o características."
                        error={formErrors.descripcion}
                      />
                    </div>
                  </FormSection>

                  <FormSection
                    title="Dirección"
                    description="Ubicación administrativa del inmueble."
                  >
                    <div className="col-12 col-lg-8">
                      <BaseInput
                        label="Dirección"
                        name="direccion"
                        required
                        value={formData.direccion}
                        onChange={handleChange}
                        placeholder="Av. Principal 123, Urb. Centro"
                        error={formErrors.direccion}
                        groupEnd={
                          <BaseButton type="button" variant="outline" onClick={handleGeocodeAddress}>
                            Buscar en mapa
                          </BaseButton>
                        }
                      />
                    </div>

                    <div className="col-12 col-lg-4">
                      <BaseInput
                        label="Ubigeo"
                        name="ubigeo"
                        required
                        restrict="number"
                        maxLength={6}
                        value={formData.ubigeo}
                        onChange={handleChange}
                        placeholder="120101"
                        error={formErrors.ubigeo}
                      />
                    </div>
                  </FormSection>

                  <FormSection
                    title="Ubicación en mapa"
                    description="Marca el punto exacto del edificio. Se usará en el mapa público de RentAHAB."
                  >
                    <div className="col-12">
                      <div className="inmueble-form__location-grid">
                        <div className="inmueble-form__map-panel">
                          <BaseInput
                            label="Ciudad"
                            name="city"
                            value={formData.city}
                            onChange={handleChange}
                            placeholder="Huancayo"
                          />
                          <BaseInput
                            label="Provincia / Departamento"
                            name="province"
                            value={formData.province}
                            onChange={handleChange}
                            placeholder="Junín"
                          />
                          <BaseInput
                            label="País"
                            name="country"
                            value={formData.country}
                            onChange={handleChange}
                            placeholder="Perú"
                          />
                          <label className="inmueble-form__map-toggle">
                            <input
                              type="checkbox"
                              name="show_on_map"
                              checked={formData.show_on_map}
                              onChange={handleChange}
                            />
                            <div>
                              <strong>Mostrar en mapa público</strong>
                              <span>Visible en el landing de RentAHAB para clientes potenciales.</span>
                            </div>
                          </label>
                        </div>

                        <div>
                          <LocationMapPicker
                            latitude={formData.latitude}
                            longitude={formData.longitude}
                            onChange={handleMapLocationChange}
                            height={360}
                          />
                          {formErrors.latitude && (
                            <div className="text-danger mt-2" style={{ fontSize: 13 }}>
                              {formErrors.latitude}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </FormSection>

                  <div className="inmueble-form__actions ui-form-actions">
                    <BaseButton type="button" variant="secondary" onClick={handleCancel}>
                      Cancelar
                    </BaseButton>
                    <BaseButton type="submit" variant="primary">
                      Guardar inmueble
                    </BaseButton>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AnadirInmueble;
